/** User-facing catalog of canvas assistant actions (Actions tab). */

export const ASSISTANT_ACTION_CATALOG_SECTIONS = [
    {
        section: "Commands",
        intro:
            "Direct canvas actions—panels, view toggles, selection, files. No API calls or domain reasoning required. You can chain multiple commands in one request and the assistant will sequence them without asking for confirmation.",
        groups: [
            {
                group: "Library & files",
                actions: [
                    {
                        id: "open_my_library",
                        label: "Open My library",
                        description: "Browse graphs saved in this browser.",
                        examples: ["Open My library", "Show my saved graphs"],
                    },
                    {
                        id: "open_library_graph",
                        label: "Open saved graph",
                        description: "Load a named graph from My library onto the canvas.",
                        examples: [
                            "Open my Waist-hip ratio graph",
                            "Load saved graph Type 2 diabetes study",
                        ],
                    },
                    {
                        id: "save_graph",
                        label: "Save to library",
                        description: "Save the current graph layout to My library.",
                        examples: ["Save this graph to my library"],
                    },
                    {
                        id: "import_graph",
                        label: "Import graph",
                        description: "Open the import-graph flow.",
                        examples: ["Import a saved graph"],
                    },
                    {
                        id: "export_graph",
                        label: "Export graph",
                        description: "Download the current workflow JSON.",
                        examples: ["Export the graph"],
                    },
                    {
                        id: "new_graph",
                        label: "New graph",
                        description: "Start a new graph from scratch.",
                        examples: ["Start a new graph", "Reset the graph"],
                    },
                    {
                        id: "download_snapshot",
                        label: "Download snapshot",
                        description: "Download an HTML snapshot of the current graph.",
                        examples: ["Download a graph snapshot"],
                    },
                ],
            },
            {
                group: "Panels & view",
                actions: [
                    {
                        id: "open_filter_panel",
                        label: "Open filter panel",
                        description: "Open the visibility filter panel without running a filter.",
                        examples: ["Open the filter panel", "Show visibility filters"],
                    },
                    {
                        id: "close_filter_panel",
                        label: "Close filter panel",
                        description: "Close the visibility filter panel.",
                        examples: ["Close the filter panel", "Close filter panel"],
                    },
                    {
                        id: "open_expand_panel",
                        label: "Open Expand KG",
                        description: "Open the Expand KG panel with seeds prefilled (no fetch yet).",
                        examples: [
                            "Open expand panel for selected nodes",
                            "Open Expand KG from BRCA1",
                        ],
                    },
                    {
                        id: "close_expand_panel",
                        label: "Close Expand KG",
                        description: "Close the Expand KG panel.",
                        examples: ["Close the expand panel", "Close Expand KG"],
                    },
                    {
                        id: "open_expansion_history",
                        label: "Expansion history",
                        description: "Open the Expand KG panel on the History tab to review past expansion runs.",
                        examples: [
                            "Show expansion history",
                            "Open expansion history",
                        ],
                    },
                    {
                        id: "toggle_data_table",
                        label: "Data table",
                        description: "Open or close the graph data table panel.",
                        examples: ["Open the data table", "Close the data table"],
                    },
                    {
                        id: "set_jumping_edges_visible",
                        label: "Jumping edges",
                        description: "Show or hide hopping / jumping edges.",
                        examples: ["Show jumping edges", "Hide jumping edges"],
                    },
                    {
                        id: "set_contextual_edges_visible",
                        label: "Contextual edges",
                        description: "Show or hide dashed contextual edges.",
                        examples: ["Show contextual edges", "Hide contextual edges"],
                    },
                    {
                        id: "focus_graph_view",
                        label: "Focus view",
                        description: "Pan and zoom to selected nodes or fit the whole graph.",
                        examples: [
                            "Zoom to BRCA1",
                            "Fit the whole graph in view",
                        ],
                    },
                ],
            },
            {
                group: "Selection & cleanup",
                actions: [
                    {
                        id: "select_nodes",
                        label: "Select nodes",
                        description:
                            "Mark nodes as selected (blue). Selecting a single named node runs immediately; top-N or filter-based picks are planned step by step.",
                        examples: [
                            "Select NMU",
                            "Select BRCA1",
                            "Select top 5 genes connected to Type 2 diabetes",
                            "Select nodes that passed the last filter",
                            "Select nodes that failed the last filter",
                        ],
                    },
                    {
                        id: "unselect_nodes",
                        label: "Unselect nodes",
                        description:
                            "Remove nodes from the current selection without deleting them from the graph.",
                        examples: [
                            "Unselect all",
                            "Unselect all visible genes",
                            "Unselect all visible traits",
                            "Unselect all visible gene sets",
                        ],
                    },
                    {
                        id: "select_visible_nodes",
                        label: "Select visible nodes",
                        description:
                            "Mark all nodes currently shown on the canvas as selected (respects visibility filters).",
                        examples: [
                            "Select all visible nodes",
                            "Select all visible genes",
                            "Select all visible traits",
                            "Select all visible gene sets",
                        ],
                    },
                    {
                        id: "remove_node",
                        label: "Remove node",
                        description: "Remove one or more nodes from the graph.",
                        examples: [
                            "Remove BRCA1 from the graph",
                            "Remove TP53 from the graph",
                        ],
                    },
                    {
                        id: "remove_invisible_nodes",
                        label: "Remove hidden nodes",
                        description: "Delete all nodes currently hidden by visibility filters.",
                        examples: ["Remove invisible nodes", "Delete hidden nodes from the graph"],
                    },
                    {
                        id: "inspect",
                        label: "Inspect",
                        description: "Open the inspector for a node or edge on the graph.",
                        examples: ["Inspect BRCA1", "Inspect the edge between TP53 and BRCA1"],
                    },
                    {
                        id: "select_connected_nodes",
                        label: "Select connected nodes",
                        description:
                            "Mark a seed node and every node directly linked by active or contextual edges. Adds to the current selection.",
                        examples: [
                            "Select all nodes connected to FLG",
                            "Select connected nodes for BRCA1",
                        ],
                    },
                ],
            },
        ],
    },
    {
        section: "Research",
        intro:
            "Requests that search the catalog, call the API, or require reasoning about which nodes are relevant. The assistant plans these step by step and will ask for clarification if your request is underspecified.",
        groups: [
            {
                group: "Add nodes",
                actions: [
                    {
                        id: "add_node",
                        label: "Add node",
                        description:
                            "Search the catalog and add up to 20 nodes per step. Nodes appear first; edges are rebuilt automatically.",
                        examples: [
                            "Add TP53 to the graph",
                            "Add 5 gene sets matching insulin signaling",
                            "Add 10 traits matching type 2 diabetes",
                            "Add 5 mechanisms matching insulin resistance",
                        ],
                    },
                    {
                        id: "add_nodes_by_intent",
                        label: "Add nodes by intention",
                        description:
                            "Describe a research goal; the assistant plans catalog searches and adds up to 20 gene sets, mechanisms, and traits per step.",
                        examples: [
                            "Find a glycosylation mechanism that could alter lipoprotein handling and coagulation",
                            "Add nodes related to adipose expansion and adverse metabolic outcomes",
                        ],
                    },
                    {
                        id: "add_gene_set_crossing",
                        label: "Add crossing gene sets",
                        description:
                            "Search and add intersection gene sets (e.g. GTEx ∩ LINCS) in one semantic query.",
                        examples: [
                            "Add gene sets for pancreas malfunction from GTEx crossed with LINCS",
                            "Find LINCS PDGFRL CRISPR KO crossed with MoTrPAC vastus lateralis training",
                            "Add intersection gene sets for adipose GTEx and LINCS",
                        ],
                    },
                    {
                        id: "add_phenotype_gene_sets",
                        label: "Add phenotype gene sets",
                        description:
                            "Search trait–gene set associations by research question and add matching traits and gene sets to the graph.",
                        examples: [
                            "Find traits and gene sets for insulin secretion in pancreatic beta cells",
                            "Add nodes related to adipose expansion and adverse metabolic outcomes",
                            "Search phenotype gene sets for type 2 diabetes complications",
                        ],
                    },
                ],
            },
            {
                group: "Expand & connect",
                actions: [
                    {
                        id: "expand_graph",
                        label: "Expand graph",
                        description:
                            "Fetch and add up to 20 neighbor nodes per step from selected seeds or a named node. Includes trait→gene set expansion when you name a trait and count.",
                        examples: [
                            "Expand from selected genes",
                            "Add 10 neighbor mechanisms to BRCA1",
                            "Expand 15 gene set nodes from Type 2 diabetes",
                            "Expand neighbors from the edge between TP53 and BRCA1",
                        ],
                    },
                ],
            },
            {
                group: "Filter",
                actions: [
                    {
                        id: "filter_graph",
                        label: "Filter graph",
                        description:
                            "Build or toggle visibility filters by intent, novelty, or expression.",
                        examples: [
                            "Filter genes related to insulin resistance",
                            "Enable the last visibility filter",
                            "Disable the last visibility filter",
                            "Show only novel nodes",
                            "Show only known nodes",
                        ],
                    },
                ],
            },
            {
                group: "Analyze",
                actions: [
                    {
                        id: "explain_graph",
                        label: "Explain graph",
                        description:
                            "Generate an LLM summary of selected nodes or all visible nodes.",
                        examples: [
                            "Explain selected nodes",
                            "Explain all visible nodes on the graph",
                        ],
                    },
                    {
                        id: "build_hypotheses",
                        label: "Build hypotheses",
                        description:
                            "Rank gene → mechanism → trait pathways and generate hypotheses.",
                        examples: [
                            "Build hypotheses for known pathways only",
                            "Build hypotheses for selected nodes",
                        ],
                    },
                    {
                        id: "find_datasets",
                        label: "Find related datasets",
                        description: "Fetch CFDE gene sets matched to selected genes via the dataset search API.",
                        examples: [
                            "Find related datasets",
                            "Find CFDE gene sets for selected genes",
                        ],
                    },
                    {
                        id: "map_genes",
                        label: "Map genes",
                        description:
                            "Fetch gene membership for selected gene sets and show shared genes in a membership table.",
                        examples: [
                            "Map genes across selected gene sets",
                            "Show shared genes in selected gene sets",
                        ],
                    },
                    {
                        id: "open_provenance_explorer",
                        label: "Provenance explorer",
                        description:
                            "Fetch and copy provenance details for selected gene sets, then open the provenance explorer.",
                        examples: [
                            "Open provenance explorer for selected gene sets",
                            "Copy gene set information and open provenance explorer",
                        ],
                    },
                ],
            },
        ],
    },
];

export function flattenActionCatalog(sections = ASSISTANT_ACTION_CATALOG_SECTIONS) {
    return (sections || []).flatMap((section) => section.groups || []);
}

/** Flat group list (legacy shape for helpers that expect top-level groups). */
export const ASSISTANT_ACTION_CATALOG = flattenActionCatalog();

export function catalogActionIds(catalog = ASSISTANT_ACTION_CATALOG) {
    return (catalog || []).flatMap((group) => group.actions.map((action) => action.id));
}

export function defaultProgressMessageForAction(action) {
    switch (action) {
        case "expand_graph":
            return "Expanding graph…";
        case "filter_graph":
            return "Building visibility filter…";
        case "explain_graph":
            return "Generating graph explanation…";
        case "build_hypotheses":
            return "Ranking pathways…";
        case "find_datasets":
            return "Finding related datasets…";
        case "map_genes":
            return "Mapping shared genes…";
        case "open_provenance_explorer":
            return "Opening provenance explorer…";
        case "select_connected_nodes":
            return "Selecting connected nodes…";
        case "select_nodes":
            return "Updating selected nodes…";
        case "select_visible_nodes":
            return "Selecting visible nodes…";
        case "unselect_nodes":
            return "Updating selection…";
        case "open_expand_panel":
            return "Opening Expand KG…";
        case "open_expansion_history":
            return "Opening expansion history…";
        case "close_filter_panel":
            return "Closing filter panel…";
        case "close_expand_panel":
            return "Closing Expand KG…";
        case "focus_graph_view":
            return "Adjusting graph view…";
        case "add_node":
            return "Adding node…";
        case "add_nodes_by_intent":
            return "Finding nodes from your intention…";
        case "add_gene_set_crossing":
            return "Searching crossing gene sets…";
        case "add_phenotype_gene_sets":
            return "Searching trait–gene set associations…";
        case "remove_node":
            return "Removing nodes…";
        case "remove_invisible_nodes":
            return "Removing hidden nodes…";
        case "open_library_graph":
            return "Loading saved graph…";
        default:
            return "Running assistant action…";
    }
}

export function assistantActionUsesExpandProgress(action) {
    return action === "expand_graph";
}

export function assistantActionShowsProgressOverlay(action, options = {}) {
    if (action === "expand_graph") {
        return true;
    }
    if (action === "filter_graph") {
        return (options.mode || "build") === "build";
    }
    return [
        "explain_graph",
        "build_hypotheses",
        "find_datasets",
        "map_genes",
        "open_provenance_explorer",
    ].includes(action);
}

export function assistantActionUsesExpandPanel(action) {
    return action === "open_expand_panel" || action === "expand_graph";
}

export function assistantStepClosesExpandPanel(action) {
    return action === "expand_graph";
}

export function assistantStepClosesFilterPanel(action, options = {}) {
    if (action === "filter_graph") {
        return (options.mode || "build") === "build";
    }
    return false;
}

export function assistantStepReopensAssistantPanel(action, options = {}) {
    return (
        assistantStepClosesExpandPanel(action) ||
        assistantStepClosesFilterPanel(action, options)
    );
}
